package com.example.hukakmp.data

import com.example.hukakmp.chat.ChatPersistence
import com.example.hukakmp.counter.FishCounterPersistence
import com.example.hukakmp.draft.ReportDraftPersistence
import com.example.hukakmp.model.HukaUser
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.GoogleAuthProvider as FirebaseGoogleAuthProvider
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

sealed class AuthState {
    data object Loading : AuthState()
    data object Unauthenticated : AuthState()
    data class Authenticated(val user: HukaUser) : AuthState()
    data class Error(val message: String) : AuthState()
}

private fun FirebaseUser.toHukaUser() = HukaUser(
    uid = uid,
    email = email,
    displayName = displayName,
    photoUrl = photoURL,
)

class AuthRepository {
    private val auth = Firebase.auth

    val authState: Flow<AuthState> = auth.authStateChanged.map { user ->
        if (user == null) AuthState.Unauthenticated else AuthState.Authenticated(user.toHukaUser())
    }

    suspend fun signInWithGoogleIdToken(idToken: String): AuthState = try {
        val credential = FirebaseGoogleAuthProvider.credential(idToken, null)
        val result = auth.signInWithCredential(credential)
        val user = result.user ?: return AuthState.Error("Error en Firebase: usuario nulo")
        AuthState.Authenticated(user.toHukaUser())
    } catch (e: Exception) {
        AuthState.Error(authErrorMessage(e))
    }

    suspend fun signInWithEmailAndPassword(email: String, password: String): AuthState = try {
        val result = auth.signInWithEmailAndPassword(email.trim(), password)
        val user = result.user ?: return AuthState.Error("No se pudo recuperar el usuario autenticado.")
        AuthState.Authenticated(user.toHukaUser())
    } catch (e: Exception) {
        AuthState.Error(authErrorMessage(e))
    }

    suspend fun createUserWithEmailAndPassword(
        displayName: String,
        email: String,
        password: String,
    ): AuthState = try {
        val result = auth.createUserWithEmailAndPassword(email.trim(), password)
        val user = result.user ?: return AuthState.Error("No se pudo crear el usuario.")
        if (displayName.isNotBlank()) {
            user.updateProfile(displayName = displayName.trim())
        }
        AuthState.Authenticated(user.toHukaUser().copy(displayName = displayName.trim().ifBlank { user.displayName }))
    } catch (e: Exception) {
        AuthState.Error(authErrorMessage(e))
    }

    /** Devuelve null cuando el correo de recuperación fue enviado correctamente. */
    suspend fun sendPasswordResetEmail(email: String): String? = try {
        auth.sendPasswordResetEmail(email.trim())
        null
    } catch (e: Exception) {
        authErrorMessage(e)
    }

    suspend fun signOut() {
        val uid = auth.currentUser?.uid
        if (uid != null) {
            runCatching { ChatPersistence.clear(uid) }
            runCatching { ReportDraftPersistence.clear(uid) }
        }
        runCatching { FishCounterPersistence.clear() }
        auth.signOut()
    }

    private fun authErrorMessage(error: Exception): String {
        val raw = error.message.orEmpty()
        val normalized = raw.lowercase()
        return when {
            "invalid-email" in normalized || "badly formatted" in normalized ->
                "El correo electrónico no es válido."
            "wrong-password" in normalized || "invalid-credential" in normalized ||
                "invalid credential" in normalized ->
                "Correo o contraseña incorrectos."
            "user-not-found" in normalized ->
                "No existe una cuenta con ese correo."
            "email-already-in-use" in normalized || "already in use" in normalized ||
                "already in use by another account" in normalized ->
                "Ya existe una cuenta con ese correo."
            "weak-password" in normalized || "password should be" in normalized ->
                "La contraseña es demasiado débil. Usá al menos 6 caracteres."
            "network" in normalized ->
                "No se pudo conectar. Revisá tu conexión a Internet."
            "too-many-requests" in normalized ->
                "Hubo demasiados intentos. Esperá unos minutos y volvé a probar."
            else -> "No se pudo completar la operación. Intentá nuevamente."
        }
    }
}
